import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Modal, Typography, Button, TextField, Skeleton, CircularProgress } from '@ann/web-components';
import {
  ModalWrapper,
  GroupsGridWrapper,
  SelectedItemsWrapper,
  SelectedItemsContent,
  SelectedItemsAction,
  SelectedGroupsText,
  ModalBodyContent,
  ContentHeader,
  SearchFieldWrapper,
  LoadMoreButtonContainer,
  LoadingContainer
} from './AddUsersModals.styled';
import { ModalListCard } from '../../components';
import { useBackendPagination } from '../../../../hooks/useBackendPagination';
import { getGroupList, assignGroups } from '../../../../services';
import { useToast } from '../../../../../../../components/toast';

const AddUsersToGroupsModal = ({selectedUsers = [], onClose, isOpen = false}) => {
  const toast = useToast();
  const [selectedGroups, setSelectedGroups] = useState(new Set());
  const [searchValue, setSearchValue] = useState("");
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const resetRef = useRef(null);

  // Log userUuid when modal opens
  useEffect(() => {
    if (isOpen && selectedUsers.length > 0) {
      // Programación defensiva: soportar ambos formatos (uuid y userUuid)
      const userUuid = selectedUsers[0].uuid || selectedUsers[0].userUuid || selectedUsers[0].id;
      // userUuid available for future use
    }
  }, [isOpen, selectedUsers]);

  // Configure fetch function for getGroupList - memoized to avoid recreating on each render
  const fetchGroups = useCallback(async (params) => {
    try {
      const response = await getGroupList(params);
      const statusCode = response.status_code || response.statusCode || 200;
      const responseBody = response.body || [];
      const meta = response.meta || {};

      // Normalize meta to ensure has_next is always present
      const normalizedMeta = {
        ...meta,
        has_next: meta.has_next !== undefined ? meta.has_next : (meta.hasNext || false)
      };

      if (statusCode >= 200 && statusCode < 300) {
        return {
          items: Array.isArray(responseBody) ? responseBody : [],
          meta: normalizedMeta
        };
      } else {
        throw new Error(response.msg_code?.[0]?.description || 'Failed to fetch groups');
      }
    } catch (error) {
      throw error;
    }
  }, []);

  const ITEMS_PER_PAGE = 8;

  // Use backend pagination hook
  const {
    items: allGroups,
    isLoading,
    error,
    hasMore,
    loadMore,
    reset,
    meta,
  } = useBackendPagination(
    fetchGroups,
    {
      order_by: ["name"],
      order_direction: ["ASC"]
    },
    {
      itemsPerPage: ITEMS_PER_PAGE,
      autoLoadFirstPage: isOpen, // Only auto-load when modal is open
      onError: (error) => {
        const errorMessage = error?.message || 'Error loading groups. Please try again later.';
        toast.error({ 
          message: errorMessage, 
          duration: 4000, 
          canClose: true 
        });
      }
    }
  );

  // Store reset function in ref for use in debounced search
  useEffect(() => {
    resetRef.current = reset;
  }, [reset]);

  // Load groups when modal opens
  useEffect(() => {
    if (isOpen) {
      reset().catch(() => {
        // Error already handled in onError callback
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Build search params based on searchValue
  const buildSearchParams = useCallback((keyword) => {
    if (keyword && keyword.trim()) {
      return { search: { keyword: keyword.trim() } };
    }
    return { search: undefined };
  }, []);

  // Helper function to reset modal state
  const resetModalState = useCallback(() => {
    setSelectedGroups(new Set());
    setSearchValue("");
    setIsLoadingMore(false);
  }, []);

  // Update search params when search value changes (with debounce)
  useEffect(() => {
    if (!isOpen) return;
    
    const timeoutId = setTimeout(() => {
      const searchParams = buildSearchParams(searchValue);
      resetRef.current?.({
        ...searchParams,
        order_by: ["name"],
        order_direction: ["ASC"]
      }).catch(() => {
        // Error already handled in onError callback
      });
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchValue, isOpen, buildSearchParams]);

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      resetModalState();
    }
  }, [isOpen, resetModalState]);

  const handleAddGroups = useCallback(async () => {
    if (selectedGroups.size === 0 || selectedUsers.length === 0 || isAssigning) return;

    try {
      setIsAssigning(true);

      // Extract userUuids from selectedUsers
      // Programación defensiva: soportar ambos formatos (uuid y userUuid)
      const userUuids = selectedUsers
        .map(user => user.uuid || user.userUuid || user.id)
        .filter(Boolean);

      // Extract groupUuids from selectedGroups
      const groupUuids = Array.from(selectedGroups);

      if (userUuids.length === 0) {
        throw new Error("No valid user UUIDs found");
      }

      if (groupUuids.length === 0) {
        throw new Error("No groups selected");
      }

      await assignGroups(userUuids, groupUuids);

      toast.success({
        message: `Group${groupUuids.length === 1 ? '' : 's'} assigned successfully to ${selectedUsers.length} ${selectedUsers.length === 1 ? 'user' : 'users'}`,
        canClose: true
      });

      resetModalState();
      onClose?.();
    } catch (error) {
      console.error("Error assigning groups:", error);

      toast.error({
        message: error.message || "Error assigning groups",
        canClose: true
      });
    } finally {
      setIsAssigning(false);
    }
  }, [selectedGroups, selectedUsers, toast, resetModalState, onClose, isAssigning]);

  const handleClose = useCallback(() => {
    resetModalState();
    onClose?.();
  }, [resetModalState, onClose]);

  const handleClearSelection = useCallback(() => {
    setSelectedGroups(new Set());
  }, []);

  const handleLoadMore = useCallback(async () => {
    try {
      setIsLoadingMore(true);
      await loadMore();
    } catch (error) {
      // Error already handled in onError callback
    } finally {
      setIsLoadingMore(false);
    }
  }, [loadMore]);

  const handleSearchChange = useCallback((value) => {
    setSearchValue(value || "");
  }, []);

  const handleGroupToggle = useCallback((groupId) => {
    setSelectedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  }, []);

  // Transform API groups to card format
  const listGroupsCards = useMemo(() => {
    return allGroups.map((group) => ({
      id: group.uuid || group.groupUuid, // API returns 'uuid', fallback to 'groupUuid' for compatibility
      title: group.name,
      description: group.description || ""
    }));
  }, [allGroups]);

  // Get all selected group titles - optimized with Map for O(1) lookup
  const selectedGroupTitles = useMemo(() => {
    if (selectedGroups.size === 0) return [];
    
    // Create a Map for O(1) lookup instead of O(n) find
    const groupsMap = new Map(allGroups.map(g => [g.uuid || g.groupUuid, g.name]));
    
    return Array.from(selectedGroups)
      .map(groupId => groupsMap.get(groupId) || groupId);
  }, [selectedGroups, allGroups]);

  // Calculate total items from meta
  const totalItems = meta?.total_items || allGroups.length;

  // Reusable loading spinner
  const loadingSpinner = (
    <LoadingContainer>
      <CircularProgress size="xl" />
    </LoadingContainer>
  );

  return (
    <ModalWrapper>
      <Modal isOpen={isOpen} onClose={handleClose} size="medium">
        <Modal.Header>
          <Modal.Header.Title>
            Add users to groups
          </Modal.Header.Title>
          <Modal.Header.Description>
            <Typography category="body" size="medium" weight="regular" color="body">
              Organize users by team or department to streamline collaboration and permission management.
            </Typography>
          </Modal.Header.Description>
        </Modal.Header>

        <Modal.Body>
          <ModalBodyContent>
            <ContentHeader id={'header-c'}>
              <SearchFieldWrapper>
                <TextField
                  iconLeft="search"
                  onChange={handleSearchChange}
                  size="small"
                  placeholder="Search"
                  value={searchValue}
                  disabled={isLoading}
                />
              </SearchFieldWrapper>

              <Typography category="body" size="medium" weight="regular" color="body">
                Showing {listGroupsCards.length} of {totalItems} {totalItems === 1 ? 'group' : 'groups'}
              </Typography>
            </ContentHeader>

            <GroupsGridWrapper>
              {isLoading && listGroupsCards.length === 0 ? (
                loadingSpinner
              ) : (
                <>
                  {/* Show CircularProgress when searching/filtering (not Load More) */}
                  {isLoading && !isLoadingMore && listGroupsCards.length > 0 && loadingSpinner}

                  {/* Hide cards when searching, show when loading more */}
                  {(!isLoading || isLoadingMore) && (
                    <>
                      {listGroupsCards.map((group) => (
                        <ModalListCard
                          key={group.id}
                          id={group.id}
                          title={group.title}
                          description={group.description}
                          isChecked={selectedGroups.has(group.id)}
                          onChange={() => handleGroupToggle(group.id)}
                        />
                      ))}
                    </>
                  )}
                  
                  {/* Show skeletons when loading more */}
                  {isLoadingMore && (
                    <>
                      {Array.from({ length: ITEMS_PER_PAGE }).map((_, i) => (
                        <Skeleton key={`loading-skeleton-${i}`} height="72px" width="100%" />
                      ))}
                    </>
                  )}

                  {/* Show Load More button when not loading and has more */}
                  {!isLoading && !isLoadingMore && hasMore && (
                    <LoadMoreButtonContainer>
                      <Button
                        size="medium"
                        type="tertiary"
                        onClick={handleLoadMore}
                        label="Load More"
                      />
                    </LoadMoreButtonContainer>
                  )}
                </>
              )}
            </GroupsGridWrapper>

            <SelectedItemsWrapper selectedGroups={selectedGroups.size > 0}>
              <SelectedItemsContent>
                <Typography category="subtitle" size="small" weight="bold" >
                  Selected groups ({selectedGroups.size})
                </Typography>
                <SelectedGroupsText>
                  {selectedGroupTitles.join(" · ")}
                </SelectedGroupsText>
              </SelectedItemsContent>
              <SelectedItemsAction>
                <Button 
                  label="Clear selection" 
                  size="small" 
                  type="tertiary" 
                  maxCharacters={40} 
                  disableEffects={true}
                  onClick={handleClearSelection}
                />
              </SelectedItemsAction>
            </SelectedItemsWrapper>

          </ModalBodyContent>
        </Modal.Body>
        <Modal.Actions align="end">
          <Modal.Button
            label="Cancel"
            onClick={handleClose}
            type="secondary"
            disabled={isAssigning}
          />
          <Modal.Button
            label={isAssigning ? "Assigning..." : `Add ${selectedGroups.size} ${selectedGroups.size === 1 ? 'group' : 'groups'}`}
            onClick={handleAddGroups}
            disabled={selectedGroups.size === 0}
          />
        </Modal.Actions>
      </Modal>
    </ModalWrapper>
  );
}

export default AddUsersToGroupsModal;